import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    TextField,
    Tooltip,
    Divider,
    alpha,
    useTheme,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';

interface WorkerMemoProps {
    memo: string | null;
    onSave: (memo: string) => Promise<void>;
    disabled?: boolean;
}

const WorkerMemo: React.FC<WorkerMemoProps> = ({ memo, onSave, disabled = false }) => {
    const theme = useTheme();
    const authUser = useSelector((state: RootState) => state.auth.user);
    const [isEditing, setIsEditing] = useState(false);
    const [memoText, setMemoText] = useState(memo || '');
    const [originalMemo, setOriginalMemo] = useState(memo || '');
    const [isSaving, setIsSaving] = useState(false);
    const textFieldRef = useRef<HTMLTextAreaElement>(null);
    const lastHeaderRef = useRef<string>('');

    useEffect(() => {
        setMemoText(memo || '');
        setOriginalMemo(memo || '');
    }, [memo]);

    useEffect(() => {
        if (isEditing && textFieldRef.current) {
            // Focus and position cursor two lines below the newly inserted header
            const headerLength = lastHeaderRef.current.length || getHeaderText().length;
            const caretPos = Math.max(0, headerLength - 2); // place caret two newlines before the end of header
            textFieldRef.current.focus();
            textFieldRef.current.setSelectionRange(caretPos, caretPos);
        }
    }, [isEditing]);

    const getHeaderText = () => {
        const now = new Date();
        const dateTime = format(now, 'yyyy-MM-dd HH:mm:ss');
        // Resolve current user from Redux auth state; fallback to localStorage legacy if present
        const legacyUser = (() => {
            try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
        })();
        const accountName = (
            authUser?.name ||
            (authUser?.firstName || authUser?.lastName ? `${authUser?.firstName ?? ''} ${authUser?.lastName ?? ''}`.trim() : '') ||
            authUser?.username ||
            authUser?.email ||
            legacyUser.name ||
            legacyUser.username ||
            legacyUser.email ||
            'Unknown User'
        );
        // End with 4 newlines so the cursor lands two blank lines below the header
        // and leaves clear separation from prior entries
        return `=== ${accountName} ${dateTime} ===\n\n\n\n`;
    };

    const handleEditClick = () => {
        if (!disabled) {
            const header = getHeaderText();
            lastHeaderRef.current = header;
            // Prepend the new header so newest entry is on top
            setMemoText((prev) => {
                const existing = prev || '';
                // Always put the newest header first; header already ends with two newlines
                return header + existing;
            });
            setIsEditing(true);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(memoText);
            setOriginalMemo(memoText);
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to save memo:', error);
            // Keep editing mode open on error
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setMemoText(originalMemo);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Save on Ctrl+S or Cmd+S
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
            e.preventDefault();
            handleSave();
        }
        // Cancel on Escape
        if (e.key === 'Escape') {
            handleCancel();
        }
    };

    return (
        <Paper
            sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
        >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <WhatsAppIcon color="success" />
                    <Typography variant="subtitle1" fontWeight="medium">
                        Memo / Notes
                    </Typography>
                </Box>
                {!isEditing ? (
                    <Tooltip title="Edit memo">
                        <IconButton
                            size="small"
                            onClick={handleEditClick}
                            disabled={disabled}
                            sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Save (Ctrl+S)">
                            <IconButton
                                size="small"
                                onClick={handleSave}
                                disabled={isSaving}
                                sx={{
                                    color: theme.palette.success.main,
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                                    },
                                }}
                            >
                                <SaveIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel (Esc)">
                            <IconButton
                                size="small"
                                onClick={handleCancel}
                                disabled={isSaving}
                                sx={{
                                    color: theme.palette.error.main,
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    },
                                }}
                            >
                                <CancelIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {isEditing ? (
                <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    maxRows={20}
                    value={memoText}
                    onChange={(e) => setMemoText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    inputRef={textFieldRef}
                    disabled={isSaving}
                    placeholder="Enter your notes here..."
                    sx={{
                        '& .MuiInputBase-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            backgroundColor: alpha(theme.palette.background.default, 0.5),
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.divider, 0.2),
                        },
                    }}
                />
            ) : (
                <Box
                    sx={{
                        minHeight: 100,
                        maxHeight: 400,
                        overflowY: 'auto',
                        p: 2,
                        backgroundColor: alpha(theme.palette.background.default, 0.3),
                        borderRadius: 1,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                >
                    {memoText ? (
                        <Typography
                            variant="body2"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontFamily: 'monospace',
                                color: theme.palette.text.primary,
                            }}
                        >
                            {memoText}
                        </Typography>
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.disabled,
                                fontStyle: 'italic',
                            }}
                        >
                            No notes available. Click the edit button to add notes.
                        </Typography>
                    )}
                </Box>
            )}

            {isEditing && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mt: 1,
                        color: theme.palette.text.secondary,
                        fontStyle: 'italic',
                    }}
                >
                    Tip: Press Ctrl+S to save, Esc to cancel
                </Typography>
            )}
        </Paper>
    );
};

export default WorkerMemo;
